import { afterNextPaint } from '@helpers/after-next-paint';
import { CATEGORY_FILTER_ID } from '@pages/VendorsSearch/containers/FilterPills/atoms';
import { TextCaption } from '@tkww/orion-web-ds-text';
import { Checkbox, MultiSelectGroup } from '@xo-union/tk-component-switches';
import React, {
	type FC,
	type ChangeEvent,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { subtext, toSidebarOptionCase } from '../../../utils';
import { useToggleSidebarFilter } from '../../hooks/use-toggle-sidebar-filter';
import type { OptionProps } from '../../types';
import Styles from '../SidebarOptions/styles.scss';

interface MultiOptionProps extends OptionProps {
	collapsedCount?: number;
}

export const MultiOption: FC<MultiOptionProps> = ({
	filter,
	selectedSidebarFilters,
	collapsedCount,
	isFilterPolishEnabled,
}) => {
	const isCategoryFilter = filter.id === CATEGORY_FILTER_ID;
	const shouldSort = isFilterPolishEnabled && !isCategoryFilter;
	const orderedOptions = useMemo(() => {
		if (!shouldSort) {
			return filter.filterOptions;
		}

		const allOption = filter.filterOptions.find(
			(option) => option.name.trim().toLowerCase() === 'all',
		);
		const rest = filter.filterOptions.filter((option) => option !== allOption);
		const sorted = [...rest].sort((a, b) =>
			a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
		);

		return allOption ? [allOption, ...sorted] : sorted;
	}, [filter.filterOptions, shouldSort]);

	const hasSelectionBeyondThreshold =
		collapsedCount != null &&
		orderedOptions
			.slice(collapsedCount)
			.some((opt) => selectedSidebarFilters.includes(opt.id));
	const [expanded, setExpanded] = useState(hasSelectionBeyondThreshold);
	useEffect(() => {
		if (hasSelectionBeyondThreshold) {
			setExpanded(true);
		}
	}, [hasSelectionBeyondThreshold]);
	const toggleSidebarFilter = useToggleSidebarFilter();
	const onChange = (e: ChangeEvent<HTMLInputElement>) => {
		afterNextPaint(() => {
			const { value } = e.target;
			toggleSidebarFilter(value);
		});
	};

	const showToggle =
		collapsedCount != null && orderedOptions.length > collapsedCount;

	const visibleOptions =
		showToggle && !expanded
			? orderedOptions.slice(0, collapsedCount)
			: orderedOptions;

	return (
		<>
			<MultiSelectGroup>
				{visibleOptions.map((option) => {
					const captionText = subtext[option.id as keyof typeof subtext];
					return (
						<div className={Styles.option} key={`option_${option.id}`}>
							<Checkbox
								value={option.id}
								checked={selectedSidebarFilters.includes(option.id)}
								onChange={onChange}
							>
								{isFilterPolishEnabled
									? toSidebarOptionCase(option.name)
									: option.name}
								{captionText && (
									<TextCaption className={Styles.caption}>
										{captionText}
									</TextCaption>
								)}
							</Checkbox>
						</div>
					);
				})}
			</MultiSelectGroup>
			{showToggle && (
				<button
					type="button"
					className={Styles.toggleLink}
					aria-expanded={expanded}
					onClick={() => setExpanded(!expanded)}
				>
					{expanded ? 'Show less' : 'Show all'}
				</button>
			)}
		</>
	);
};
